/**
 * passkey-budget WP1: You › Security › "Unlock with this computer's login" (lock follows the OS).
 * Turning it on takes one passkey tap and this browser's enrollment pin for the machine; turning
 * it off needs nothing (it only removes a way to unlock).
 */
import { Button } from '@viko/transcript-ui'
import { useEffect, useState } from 'react'
import { useBackend } from '../lib/backend.js'
import { passkeyUnlock } from '../lib/idle-lock.js'
import { passkeyErrorMessage, passkeys } from '../lib/passkeys.js'
import {
  clearPresenceBinding,
  enablePresenceUnlock,
  loadPresenceBinding,
  type PresenceBinding,
} from '../lib/presence-unlock.js'

export function PresenceUnlockRow({ machineId, name }: { machineId: string; name?: string }) {
  const b = useBackend()
  const [binding, setBinding] = useState<PresenceBinding>()
  const [error, setError] = useState<string>()
  const [busy, setBusy] = useState(false)
  useEffect(() => {
    void loadPresenceBinding().then(setBinding)
  }, [])
  const on = binding?.machineId === machineId
  const toggle = async () => {
    setBusy(true)
    setError(undefined)
    try {
      if (on) {
        await clearPresenceBinding()
        setBinding(undefined)
      } else {
        const accountId = await b.content?.accountId()
        if (!accountId) throw new Error('Sign in to turn this on.')
        setBinding(
          await enablePresenceUnlock({
            accountId,
            machineId,
            verifyPasskey: passkeyUnlock(passkeys, async () => accountId),
          }),
        )
      }
    } catch (e) {
      setError(passkeyErrorMessage(e))
    } finally {
      setBusy(false)
    }
  }
  return (
    <div>
      <dt className="font-medium">Unlock with this computer’s login</dt>
      <dd className="m-0 grid gap-1 text-text-2">
        <span>
          {on
            ? `On: unlocking ${name ?? 'this computer'} (Touch ID or password) also unlocks Viko. Risky actions still need your passkey.`
            : 'Off: Viko asks for your passkey after the screen locks.'}
        </span>
        <span>
          <Button size="sm" disabled={busy} onClick={() => void toggle()}>
            {on ? 'Turn off' : 'Turn on with passkey'}
          </Button>
        </span>
        {error ? (
          <span role="alert" className="text-status-fail">
            {error}
          </span>
        ) : null}
      </dd>
    </div>
  )
}
